import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Camera, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';

const AdminScanner = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  
  useEffect(() => {
    const refInstance = html5QrCodeRef.current;
    return () => {
      // Cleanup scanner on unmount
      if (refInstance && scanning) {
        refInstance.stop().catch(console.error);
      }
    };
  }, [scanning]);
  
  const startScanning = async () => {
    setValidationResult(null);
    setScanning(true);

    // Wait for the #qr-reader div to be mounted in the DOM
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const readerEl = document.getElementById('qr-reader');
      if (!readerEl) {
        throw new Error('No se pudo inicializar el contenedor del escáner');
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Tu navegador no soporta acceso a la cámara. Abre la app en Chrome o Safari.');
      }

      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      // Helper to try starting with a given camera constraint
      const tryStart = async (cameraConfig) => {
        await html5QrCode.start(
          cameraConfig,
          config,
          async (decodedText) => {
            if (isProcessing) return;

            setIsProcessing(true);
            await validateQRCode(decodedText);

            html5QrCode.stop().then(() => {
              setScanning(false);
              setIsProcessing(false);
            }).catch(console.error);
          },
          () => {
            // Ignore scan errors (happens while focusing)
          }
        );
      };

      try {
        // Prefer the rear camera
        await tryStart({ facingMode: { exact: 'environment' } });
      } catch (rearErr) {
        console.warn('Rear camera unavailable, falling back:', rearErr);
        try {
          // Fall back to any camera
          await tryStart({ facingMode: 'environment' });
        } catch (anyErr) {
          console.warn('Facing mode failed, enumerating cameras:', anyErr);
          const cameras = await Html5Qrcode.getCameras();
          if (!cameras || cameras.length === 0) {
            throw new Error('No se detectaron cámaras disponibles en este dispositivo');
          }
          await tryStart(cameras[0].id);
        }
      }
    } catch (error) {
      console.error('Scanner error:', error);
      const message = error?.name === 'NotAllowedError' || /permission/i.test(error?.message || '')
        ? 'Permiso de cámara denegado. Habilítalo en los ajustes del navegador.'
        : error?.message || 'Error al iniciar la cámara';
      toast.error(message);
      setScanning(false);
    }
  };
  
  const validateQRCode = async (code) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scanner/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ totp_code: code })
      });
      
      const result = await response.json();
      setValidationResult(result);
      
      if (result.valid) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Error al validar código');
    }
  };
  
  const resetScanner = () => {
    setValidationResult(null);
    startScanning();
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-4xl mx-auto flex items-center space-x-4">
          <Button 
            onClick={() => navigate('/dashboard')} 
            variant="ghost" 
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-heading text-white uppercase">ESCÁNER QR</h1>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {!scanning && !validationResult && (
          <div className="text-center py-12">
            <Button
              onClick={startScanning}
              data-testid="start-scan-button"
              className="h-24 w-full max-w-md bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider text-xl shadow-[0_0_15px_rgba(229,9,20,0.3)] transition-all active:scale-95 flex items-center justify-center space-x-3"
            >
              <Camera className="h-10 w-10" />
              <span>INICIAR ESCÁNER</span>
            </Button>
          </div>
        )}
        
        {/* Scanner */}
        {scanning && (
          <Card className="bg-zinc-950 border-zinc-800 p-6">
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-white text-lg font-heading uppercase">Apunta la cámara al QR</p>
                <p className="text-zinc-400 text-sm mt-1">Centra el código en el recuadro</p>
              </div>
              
              <div id="qr-reader" className="w-full" />
              
              <Button
                onClick={() => {
                  if (html5QrCodeRef.current) {
                    html5QrCodeRef.current.stop().then(() => {
                      setScanning(false);
                    }).catch(console.error);
                  }
                }}
                variant="outline"
                className="w-full"
              >
                Cancelar
              </Button>
            </div>
          </Card>
        )}
        
        {/* Validation Result */}
        {validationResult && (
          <div className="space-y-6">
            {/* Status Banner */}
            <Card className={
              `p-6 border-2 ${
                validationResult.valid 
                  ? 'bg-green-500/10 border-green-500' 
                  : 'bg-red-500/10 border-red-500'
              }`
            } data-testid="validation-result-card">
              <div className="flex items-center justify-center space-x-3">
                {validationResult.valid ? (
                  <CheckCircle className="h-12 w-12 text-green-500" />
                ) : (
                  <XCircle className="h-12 w-12 text-red-500" />
                )}
                <div>
                  <h2 className={
                    `text-2xl font-heading uppercase ${
                      validationResult.valid ? 'text-green-500' : 'text-red-500'
                    }`
                  }>
                    {validationResult.valid ? 'ACCESO AUTORIZADO' : 'ACCESO DENEGADO'}
                  </h2>
                  <p className="text-white mt-1">{validationResult.message}</p>
                </div>
              </div>
            </Card>
            
            {/* Student Info (if available) */}
            {validationResult.student && (
              <Card className="bg-zinc-950 border-zinc-800 p-6">
                {/* Photo - Full width for verification */}
                {validationResult.student.photo_url && (
                  <div className="mb-6">
                    <img 
                      src={validationResult.student.photo_url}
                      alt={validationResult.student.name}
                      className="w-full h-64 object-cover rounded-sm"
                      data-testid="student-photo"
                    />
                  </div>
                )}
                
                {/* Student Details */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-3xl font-heading text-white uppercase" data-testid="student-name">
                      {validationResult.student.name}
                    </h3>
                    <p className="text-lg text-zinc-400">{validationResult.student.discipline}</p>
                    {validationResult.student.grade && (
                      <p className="text-zinc-500">Grado: {validationResult.student.grade}</p>
                    )}
                  </div>
                  
                  {/* Payment Status Badge */}
                  {(() => {
                    let paymentBadgeClass = 'bg-red-500/20 text-red-500';
                    if (validationResult.payment_status === 'valid') paymentBadgeClass = 'bg-green-500/20 text-green-500';
                    else if (validationResult.payment_status === 'expiring') paymentBadgeClass = 'bg-yellow-500/20 text-yellow-500';
                    return (
                      <div className="flex items-center space-x-2">
                        <span className="text-zinc-400">Estado de Pago:</span>
                        <span className={`px-3 py-1 rounded-sm text-sm font-bold uppercase ${paymentBadgeClass}`}>
                          {validationResult.payment_status === 'valid' && 'VIGENTE'}
                          {validationResult.payment_status === 'expiring' && 'POR VENCER'}
                          {validationResult.payment_status === 'expired' && 'VENCIDO'}
                        </span>
                      </div>
                    );
                  })()}
                  
                  {/* Plan Status */}
                  {validationResult.plan_status && (
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                      <span className="text-white">{validationResult.plan_status}</span>
                    </div>
                  )}
                  
                  {/* Emergency Medical Info */}
                  {(validationResult.student.blood_type || validationResult.student.emergency_contact) && (
                    <Card className="bg-red-500/10 border-red-500/30 p-4 mt-4">
                      <h4 className="text-sm font-heading text-red-400 uppercase mb-2 flex items-center space-x-2">
                        <AlertCircle className="h-4 w-4" />
                        <span>Información de Emergencia</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {validationResult.student.blood_type && (
                          <div>
                            <span className="text-zinc-400">Tipo de Sangre</span>
                            <p className="text-white font-bold text-lg">{validationResult.student.blood_type}</p>
                          </div>
                        )}
                        {validationResult.student.emergency_contact && (
                          <div>
                            <span className="text-zinc-400">Contacto de Emergencia</span>
                            <p className="text-white font-medium">{validationResult.student.emergency_contact}</p>
                            {validationResult.student.emergency_phone && (
                              <p className="text-zinc-300 font-mono">{validationResult.student.emergency_phone}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  )}
                </div>
              </Card>
            )}
            
            {/* Actions */}
            <div className="flex space-x-4">
              <Button
                onClick={resetScanner}
                data-testid="scan-next-button"
                className="flex-1 h-14 bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider"
              >
                ESCANEAR SIGUIENTE
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="h-14 px-8"
              >
                Volver
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminScanner;
